import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import styles from './index.module.css';
import { useCreateRideHandlers } from '../../../hooks/useCreateRideHandlers';
import { useDuplicateRideModal } from '../DuplicateRideModal/useDuplicateRideModal';
import { Navigation } from '../../../utils/navigation';
import { useSelector } from 'react-redux';
import { RIDE_REQUEST_FIELDS } from '../../../constants/constants';
import { connect } from 'react-redux';
import { setCurrentStep, saveDraft } from '../../../store/drafts';
import { getPlatformSettings, getAdditionalRideOptions } from '../../../store/preferences';
import {
  fetchAllHealthOrganizations,
  fetchHOPreferences,
} from '../../../store/health_organizations';
import QuickSchedule from './quickSchedule';
import QuickScheduleNew from './quickScheduleNew';
import { bindActionCreators } from 'redux';
import {
  getRideRequestFieldConfig,
  transformRideRequestFields,
} from '../../../utils';

export const QuickScheduleModal = ({
  visibleModal,
  setModalVisible,
  variant = 'new',
  step,
  draftId,
  airportBufferTime,
  draft,
  riderDetails,
  getPlatformSettings,
  platformPreferences,
  defaultTimeZone,
  fetchHOPreferences,
  HOPreferences,
  getAdditionalRideOptions,
  rideOptions,
  user,
  healthOrganizations,
  currentHealthOrganization,
  fetchAllHealthOrganizations,
  fetchHealthOrganization,
}) => {
  const [showCovidField, setShowCovidField] = useState(false);
  const [activeOrgRideRequestFields, setActiveOrgRideRequestFields] = useState(null);
  const [activeAirportBufferTime, setActiveAirportBufferTime] = useState(airportBufferTime);
  const [formikValues, setFormikValues] = useState(null);
  const [formIsValid, setFormIsValid] = useState(false);
  const [draftIsValid, setDraftIsValid] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [quickScheduleFooter, setQuickScheduleFooter] = useState(null);

  const navigate = Navigation();

  const showClinicSelect = useMemo(
    () => (user?.permissions?.orgList?.length ?? 0) > 1,
    [user?.permissions?.orgList?.length],
  );

  const orgRideRequestFields = useMemo(() => {
    if (showClinicSelect) {
      return activeOrgRideRequestFields;
    }
    return HOPreferences?.rideRequestFields;
  }, [showClinicSelect, activeOrgRideRequestFields, HOPreferences?.rideRequestFields]);

  const rideRequestFields = useMemo(
    () => transformRideRequestFields(orgRideRequestFields),
    [orgRideRequestFields],
  );

  const costCenterField = useMemo(
    () => getRideRequestFieldConfig(orgRideRequestFields, RIDE_REQUEST_FIELDS.costCenter),
    [orgRideRequestFields],
  );

  const showAdditionalSourceField = useMemo(
    () => getRideRequestFieldConfig(orgRideRequestFields, RIDE_REQUEST_FIELDS.additionalFundingSource),
    [orgRideRequestFields],
  );

  const showDocumentReasonForRideField = useMemo(
    () => getRideRequestFieldConfig(orgRideRequestFields, RIDE_REQUEST_FIELDS.documentReasonForRide),
    [orgRideRequestFields],
  );

  const syncFormikValues = useCallback((values, isValid, path) => {
    setFormikValues(values);
    setFormIsValid(prev => ({ ...prev, [path]: isValid }));
  }, []);

  const handleCancel = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setModalVisible(false);
      setIsClosing(false);
    }, 300);
  }, [setModalVisible]);
  const handleSaveRef = useRef(null);

  const { DuplicateRideModal, openDuplicateRideModal } = useDuplicateRideModal({
    onContinue: () => handleSaveRef.current?.({ skipDuplicateCheck: true }),
  });

  const {
    handleSave,
    handleQuickSubmit,
    swtichToDetailView,
  } = useCreateRideHandlers({
    Navigation: navigate,
    step,
    draftId,
    draft,
    QuickScheduleModalVisible: handleCancel,
    onDuplicatesFound: openDuplicateRideModal,
  });

  handleSaveRef.current = handleSave;

  const handleShowDetailView = useCallback((draft) => {
    swtichToDetailView(draft);

  }, [swtichToDetailView]);

  const resolveOrgRideRequestFields = useCallback(
    (organizationId) => {
      if (!organizationId) return null;

      const listedOrg = healthOrganizations?.find((org) => org.id === organizationId);
      if (listedOrg?.rideRequestFields) {
        return listedOrg.rideRequestFields;
      }

      const myOrgId = user?.organizationId || HOPreferences?.id;
      if (organizationId === myOrgId && HOPreferences?.rideRequestFields) {
        return HOPreferences.rideRequestFields;
      }

      return null;
    },
    [healthOrganizations, user?.organizationId, HOPreferences?.id, HOPreferences?.rideRequestFields],
  );

  const handleOrganizationChange = useCallback(
    (organizationId) => {
      if (!organizationId) {
        setActiveOrgRideRequestFields(null);
        return;
      }

      const fields = resolveOrgRideRequestFields(organizationId);
      setActiveOrgRideRequestFields(fields);

      const listedOrg = healthOrganizations?.find((org) => org.id === organizationId);
      if (listedOrg?.airportBufferTime != null) {
        setActiveAirportBufferTime(listedOrg.airportBufferTime);
      } else if (
        organizationId === (user?.organizationId || HOPreferences?.id) &&
        HOPreferences?.airportBufferTime != null
      ) {
        setActiveAirportBufferTime(HOPreferences.airportBufferTime);
      }
    },
    [
      healthOrganizations,
      resolveOrgRideRequestFields,
      user?.organizationId,
      HOPreferences?.id,
      HOPreferences?.airportBufferTime,
    ],
  );

  useEffect(() => {
    setShowCovidField(
      rideRequestFields?.some((field) => field.name === RIDE_REQUEST_FIELDS.covid19Relation) ?? false,
    );
  }, [rideRequestFields]);

  const clinicOrganizations = useMemo(() => {
    const list = healthOrganizations?.length
      ? healthOrganizations
      : (user?.permissions?.orgList || []).map((org) => ({
          id: org.id,
          name: org.name,
        }));

    return list.map((org) => {
      const fields = org.rideRequestFields || resolveOrgRideRequestFields(org.id);
      return {
        ...org,
        rideRequestFields: fields,
      };
    });
  }, [healthOrganizations, user?.permissions?.orgList, resolveOrgRideRequestFields]);

  useEffect(() => {
    if (!showClinicSelect && HOPreferences?.airportBufferTime != null) {
      setActiveAirportBufferTime(HOPreferences.airportBufferTime);
    }
  }, [showClinicSelect, HOPreferences?.airportBufferTime]);

  useEffect(() => {
    if (!visibleModal || !showClinicSelect || !draft?.step1?.organizationId) return;
    if (!healthOrganizations?.length) return;
    handleOrganizationChange(draft.step1.organizationId);
  }, [
    visibleModal,
    showClinicSelect,
    draft?.step1?.organizationId,
    healthOrganizations,
    handleOrganizationChange,
  ]);

  useEffect(() => {
    if (!visibleModal) return;
    fetchHOPreferences();
    if (showClinicSelect) {
      fetchAllHealthOrganizations();
      if (!draft?.step1?.organizationId) {
        setActiveOrgRideRequestFields(null);
      }
    }
    if (!platformPreferences) getPlatformSettings();
    if (!platformPreferences?.rideOptions) getAdditionalRideOptions();
  }, [
    visibleModal,
    showClinicSelect,
    platformPreferences,
    fetchHOPreferences,
    fetchAllHealthOrganizations,
    getPlatformSettings,
    getAdditionalRideOptions,
  ]);

  const isNewVariant = variant === 'new';

  const modalStyles = isNewVariant
    ? {
        container: {
          background: '#F6F6F6',
          boxShadow: '0 8px 32px #00000008',
          borderRadius: 18,
          width: 600,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'min(94vh, calc(100dvh - 24px))',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
        },
        body: {
          padding: 0,
          background: '#F6F6F6',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
        },
        footer: {
          margin: 0,
          padding: '12px 24px 24px',
          background: '#F6F6F6',
        },
      }
    : {
        container: {
          maxHeight: 'min(94vh, calc(100dvh - 24px))',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
        },
        body: {
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
        },
      };

  return (
    <>
    <Modal
      open={visibleModal}
      onCancel={handleCancel}
      closable={!isNewVariant}
      width={isNewVariant ? 600 : 680}
      centered
      destroyOnHidden
      title={null}
      styles={modalStyles}
      wrapClassName={`${styles.modalWrapper} ${isNewVariant ? styles.modalWrapperNew : styles.modalWrapperLegacy} ${isClosing ? styles.antModalSlideOut : ''}`}
      footer={isNewVariant ? quickScheduleFooter : null}
    >
      {isNewVariant ? (
        <QuickScheduleNew
          draft={draft}
          showCovidField={showCovidField}
          showClinicSelect={showClinicSelect}
          healthOrganizations={clinicOrganizations}
          onOrganizationChange={handleOrganizationChange}
          platformPreferences={platformPreferences}
          rideOptions={rideOptions}
          defaultTimeZone={defaultTimeZone}
          airportBufferTime={activeAirportBufferTime ?? airportBufferTime}
          syncFormikValues={syncFormikValues}
          handleSave={handleSave}
          handleQuickSubmit={handleQuickSubmit}
          handleShowDetailView={handleShowDetailView}
          onFooterRender={setQuickScheduleFooter}
          onClose={handleCancel}
          costCenterField={costCenterField}
          showAdditionalSourceField={showAdditionalSourceField}
          showDocumentReasonForRideField={showDocumentReasonForRideField}
        />
      ) : (
        <QuickSchedule
          step={step}
          draft={draft}
          airportBufferTime={airportBufferTime}
          platformPreferences={platformPreferences}
          defaultTimeZone={defaultTimeZone}
          handleSave={handleSave}
          riderDetails={riderDetails}
          rideRequestFields={rideRequestFields}
          syncFormikValues={syncFormikValues}
          showCovidField={showCovidField}
          handleQuickSubmit={handleQuickSubmit}
          costCenterField={costCenterField}
          showAdditionalSourceField={showAdditionalSourceField}
          showDocumentReasonForRideField={showDocumentReasonForRideField}
          handleShowDetailView={handleShowDetailView}
        />
      )}
    </Modal>
    {DuplicateRideModal}
    </>
  );
};

const mapStateToProps = (state) => ({
  step: state.drafts.currentStep,
  draftId: state.drafts.draftId,
  draft: state.drafts.draft,
  riderDetails: state.riders.riderDetails,
  user: state.authorization?.user,
  platformPreferences: state.preferences.platformPreferences,
  defaultTimeZone: state.authorization?.user?.preferredTimezone,
  HOPreferences: state.healthOrganizations.HOPreferences,
  healthOrganizations: state.healthOrganizations.healthOrganizations,
  rideOptions: state.preferences.rideOptions?.filter((option) => !option.disabled),
  userInactivityModalVisible: state.common.userInactivityModalVisible,
  airportBufferTime: state?.healthOrganizations?.HOPreferences?.airportBufferTime,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  setCurrentStep,
  saveDraft,
  getPlatformSettings,
  fetchHOPreferences,
  fetchAllHealthOrganizations,
  getAdditionalRideOptions,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(QuickScheduleModal);